import React from 'react';
import {useMatches, useLoaderData, Await} from '@remix-run/react';
import type {
  Product,
  ProductVariant,
} from '@shopify/hydrogen/storefront-api-types';

//components
import Container from 'components/Container';
import ProductDetailsBreadcrumb from './ProductDetailsBreadcrumb';
import ProductDetailsCarousel from './ProductDetailsCarousel';
import ProductDetailsVendor from './ProductDetailsVendor';
import ProductDetailsPrices from './ProductDetailsPrices';
import ProductDetailsColourOptions from './ProductDetailsColourOptions';
import ProductDetailsSizeGuide from './ProductDetailsSizeGuide';
import ProductDetailsQuantity from './ProductDetailsQuantity';
import ProductDetailsSize from './ProductDetailsSize';
import ProductDetailsCTA from './ProductDetailsCTA';
import ProductDetailsFooter from './ProductDetailsFooter';
import ProductDetailsAdditionInfo from './ProductDetailsAdditionInfo';
import FrequentlyBoughtTogether from './FrequentlyBoughtTogether';
import ProductDetailsPreorder from './ProductDetailsPreorder';
import {Skeleton} from 'components/Skeleton';

//data
import {loader} from '~/routes/($lang)/products/$productHandle';

export default function ProductDetails() {
  const matches = useMatches();
  const routeData = matches.find(
    (m) => m.id === 'routes/($lang)/products/$productHandle',
  )?.data;
  const product = routeData.product as Product & {
    selectedVariant?: ProductVariant;
  };
  // console.log(product)
  const bundles = routeData.bundles as string[];
  const firstAvailableVariant = product.variants.nodes.find(
    (v) => v.availableForSale,
  );
  const selectedVariant =
    product.selectedVariant ??
    firstAvailableVariant ??
    product.variants.nodes[0];

  const quantityAvailable =
    selectedVariant.quantityAvailable && selectedVariant.quantityAvailable > 0
      ? selectedVariant.quantityAvailable
      : selectedVariant.availableForSale
      ? 99
      : 0;
  const defaultQuantity =
    (selectedVariant.quantityAvailable &&
      selectedVariant.quantityAvailable > 0) ||
    selectedVariant.availableForSale
      ? 1
      : 0;
  const [quantity, setQuantity] = React.useState<number>(defaultQuantity);

  const {recommended, translation} = useLoaderData<typeof loader>();

  return (
    <section className="">
      <Container>
        <div className="pt-6">
          <ProductDetailsBreadcrumb />
          <div className="product-main mb-8 grid gap-4 lg:mb-[100px] lg:grid-cols-12">
            {/* SSR'd: Swiper 9 renders fine on the server (the homepage hero
                and this page's recommendations carousel already do), and the
                product images are core crawler content. */}
            <ProductDetailsCarousel selectedVariant={selectedVariant} />
            <div className="text-md lg:col-span-5 lg:text-[20px]">
              {/* text-xl (24px) mobile → text-section (28px) desktop: token
                  sizes per the typography guide instead of arbitrary px, with
                  heading-appropriate line-height. */}
              <h1 className="mb-1 text-xl md:text-section font-medium">
                {product.title}
              </h1>
              <div className="mb-[18px]">
                {selectedVariant?.sku && (
                  <span className="text-md font-normal">
                    {translation?.product_sku_label ?? 'SKU'} :{' '}
                    {selectedVariant?.sku}
                  </span>
                )}
              </div>
              <ProductDetailsVendor />
              <ProductDetailsPrices selectedVariant={selectedVariant} />
              <div className="mt-3 flex flex-col lg:mt-[20px] lg:flex-row lg:items-center">
                <ProductDetailsColourOptions
                  selectedVariant={selectedVariant}
                />
                <ProductDetailsSizeGuide />
              </div>
              <div className="mb-[28px] flex flex-col space-y-3 lg:mt-3 lg:flex-row lg:space-y-0 lg:space-x-8">
                <ProductDetailsQuantity
                  quantity={quantity}
                  setQuantity={setQuantity}
                  quantityAvailable={quantityAvailable}
                />
                <div className="order-1 lg:order-2 flex-1">
                  <ProductDetailsSize selectedVariant={selectedVariant} />
                </div>
              </div>
              <ProductDetailsCTA
                quantity={quantity}
                selectedVariant={selectedVariant}
              />
              <ProductDetailsFooter selectedVariant={selectedVariant} />
            </div>
          </div>
          <div className="product-additional mb-[100px] grid gap-8 lg:grid-cols-12 lg:gap-0">
            <div className="order-2 lg:order-1 lg:col-span-3">
              <React.Suspense fallback={<Skeleton className="h-32" />}>
                <Await
                  errorElement="There was a problem loading related products"
                  resolve={recommended}
                >
                  {(products) => {
                    return (
                      <FrequentlyBoughtTogether
                        product={products[0]}
                        type="product"
                      />
                    );
                  }}
                </Await>
              </React.Suspense>
            </div>
            <div className="order-1 lg:order-2 lg:col-span-7 lg:col-start-6">
              {/* SSR'd: this is product.descriptionHtml — the single most
                  valuable block for crawlers. dangerouslySetInnerHTML is
                  deterministic (same string server and client). */}
              <ProductDetailsAdditionInfo />
            </div>
          </div>
          {/* <div className="related-product-bundles">
            {bundles?.map((bundle_shortcode) => (
              <div className="py-10 md:py-20">
                <div
                  className="bundler-target-element"
                  data-bndlr-ccid={bundle_shortcode}
                ></div>
              </div>
            ))}
          </div> */}
        </div>
      </Container>
    </section>
  );
}
